import axios, { AxiosInstance } from 'axios';
import { runtimeConfig } from '../config/runtime';
import { Product } from '../types/product';

interface CacheStatus {
  redisAvailable: boolean;
  timestamp: string;
}

interface CacheResponse<T> {
  data?: T;
  fallbackToApi?: boolean;
  fromCache: boolean;
}

class CacheService {
  private api: AxiosInstance;
  private cacheAvailable: boolean = true;
  private lastCacheCheck: number = 0;
  private cacheCheckInterval: number = 60000; // Check every minute

  constructor() {
    this.api = axios.create({
      baseURL: '', // Will be set dynamically
      timeout: 5000, // 5 second timeout for cache requests
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and set baseURL
    this.api.interceptors.request.use(
      config => {
        // Set base URL from runtime config if not already set
        if (!config.baseURL || config.baseURL === '') {
          config.baseURL = runtimeConfig.get('API_URL');
        }

        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Check cache status on initialization
    this.checkCacheStatus();
  }

  private async checkCacheStatus(): Promise<void> {
    const now = Date.now();

    // Only check cache status if we haven't checked recently
    if (now - this.lastCacheCheck < this.cacheCheckInterval) {
      return;
    }

    try {
      this.cacheAvailable = true;
      this.lastCacheCheck = now;
    } catch (error) {
      this.cacheAvailable = false;
      this.lastCacheCheck = now;
    }
  }

  private async tryCache<T>(
    cacheEndpoint: string,
    fallbackFunction: () => Promise<T>
  ): Promise<CacheResponse<T>> {
    // Check cache status if needed
    await this.checkCacheStatus();

    // If cache is not available, go straight to fallback
    if (!this.cacheAvailable) {
      const data = await fallbackFunction();
      return { data, fromCache: false };
    }

    try {
      const response = await this.api.get(cacheEndpoint);

      // Check if the response indicates we should fallback to API
      if (response.data?.fallbackToApi) {
        const data = await fallbackFunction();
        return { data, fromCache: false };
      }

      return { data: response.data, fromCache: true };
    } catch (error: any) {
      // Mark cache as unavailable if we get connection errors
      if (error.code === 'ECONNREFUSED' || error.code === 'TIMEOUT') {
        this.cacheAvailable = false;
      }

      // Fall back to regular API
      const data = await fallbackFunction();
      return { data, fromCache: false };
    }
  }

  async getProduct(
    id: string,
    fallbackFunction: () => Promise<Product>
  ): Promise<CacheResponse<Product>> {
    return this.tryCache(`/cache/products/${id}`, fallbackFunction);
  }

  async getFeaturedProducts(
    fallbackFunction: () => Promise<Product[]>
  ): Promise<CacheResponse<Product[]>> {
    return this.tryCache('/cache/products/featured', fallbackFunction);
  }

  async getCategories(
    fallbackFunction: () => Promise<string[]>
  ): Promise<CacheResponse<string[]>> {
    return this.tryCache('/cache/categories', fallbackFunction);
  }

  async getMaterials(
    fallbackFunction: () => Promise<string[]>
  ): Promise<CacheResponse<string[]>> {
    return this.tryCache('/cache/materials', fallbackFunction);
  }

  async clearCache(pattern?: string): Promise<void> {
    try {
      const endpoint = pattern
        ? `/cache/clear?pattern=${encodeURIComponent(pattern)}`
        : '/cache/clear';
      await this.api.delete(endpoint);
    } catch (error) {
      throw error;
    }
  }

  // Admin function to check cache status
  async getCacheStatus(): Promise<CacheStatus> {
    const response = await this.api.get<CacheStatus>('/cache/status');
    return response.data;
  }

  // Property to check if cache is available
  get isAvailable(): boolean {
    return this.cacheAvailable;
  }
}

export const cacheService = new CacheService();
export default cacheService;
